import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material'
import { CheckCircle, Star, TrendingUp, People, Analytics, CreditCard } from '@mui/icons-material'
import { useSubscription } from '../hooks/useSubscription'
import { AppPageHeader } from '../components/AppPageHeader'
import { LoadingStatePanel } from '../components/StatePanel'

const PLAN_FEATURES = [
  'Unlimited athlete profiles',
  'Advanced search & filters',
  'Contact information access',
  'Analytics dashboard',
  'Priority support',
]

export const SubscriptionPage = () => {
  const { subscription, createCheckout, createPortalSession } = useSubscription()

  const sub = subscription.data
  const isActive = sub?.status === 'active'
  const isLoading = subscription.isLoading

  const handleManageBilling = async () => {
    try {
      const result = await createPortalSession.mutateAsync()
      window.location.href = result.url
    } catch {
      // Error is handled via mutation state
    }
  }

  const handleUpgrade = async () => {
    try {
      const result = await createCheckout.mutateAsync({
        successUrl: `${window.location.origin}/subscription?success=true`,
        cancelUrl: `${window.location.origin}/subscription?canceled=true`,
      })
      if (result.url) {
        window.location.href = result.url
      }
    } catch {
      // Error is handled via mutation state
    }
  }

  if (isLoading) {
    return (
      <LoadingStatePanel
        title="Loading subscription"
        description="Retrieving your current billing and subscription details."
      />
    )
  }

  return (
    <Box>
      <AppPageHeader
        title="Subscription Management"
        description="Manage your recruiter subscription and billing."
      />

      {createCheckout.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to create checkout session.
        </Alert>
      )}
      {createPortalSession.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to open billing portal.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Current Subscription */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Current Plan
                  </Typography>
                  {isActive ? (
                    <Typography variant="h3" color="primary">
                      Annual
                      <Typography component="span" variant="h6" color="text.secondary">
                        {' '}
                        subscription
                      </Typography>
                    </Typography>
                  ) : (
                    <Typography variant="h5" color="text.secondary">
                      No active subscription
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={isActive ? 'ACTIVE' : 'INACTIVE'}
                  color={isActive ? 'success' : 'default'}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Plan Features
              </Typography>
              <List>
                {PLAN_FEATURES.map((feature, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemIcon>
                      <CheckCircle color={isActive ? 'primary' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>

              <Box display="flex" gap={2} mt={3}>
                {isActive ? (
                  <Button
                    variant="contained"
                    startIcon={<CreditCard />}
                    onClick={handleManageBilling}
                    disabled={createPortalSession.isPending}
                  >
                    {createPortalSession.isPending ? 'Opening...' : 'Manage Billing'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleUpgrade}
                    disabled={createCheckout.isPending}
                  >
                    {createCheckout.isPending ? 'Redirecting...' : 'Subscribe Now'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Information */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Billing Information
              </Typography>
              {isActive && sub ? (
                <List>
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Status"
                      secondary={sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Current Period End"
                      secondary={new Date(sub.currentPeriodEnd).toLocaleDateString()}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Auto-Renew"
                      secondary={sub.cancelAtPeriodEnd ? 'Cancels at period end' : 'Yes'}
                    />
                  </ListItem>
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active subscription. Subscribe to access premium features.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Usage Statistics */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage This Month
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <People
                      sx={{
                        fontSize: 40,
                        color: isActive ? 'primary.main' : 'text.disabled',
                        mb: 1,
                      }}
                    />
                    <Typography variant="h4" color={isActive ? 'primary' : 'text.disabled'}>
                      —
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Athletes
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <TrendingUp
                      sx={{
                        fontSize: 40,
                        color: isActive ? 'primary.main' : 'text.disabled',
                        mb: 1,
                      }}
                    />
                    <Typography variant="h4" color={isActive ? 'primary' : 'text.disabled'}>
                      —
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Profile Views
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Star
                      sx={{
                        fontSize: 40,
                        color: isActive ? 'primary.main' : 'text.disabled',
                        mb: 1,
                      }}
                    />
                    <Typography variant="h4" color={isActive ? 'primary' : 'text.disabled'}>
                      —
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Saved Profiles
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Analytics
                      sx={{
                        fontSize: 40,
                        color: isActive ? 'primary.main' : 'text.disabled',
                        mb: 1,
                      }}
                    />
                    <Typography variant="h4" color={isActive ? 'primary' : 'text.disabled'}>
                      —
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      New This Week
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
